import { App } from 'antd';
import { useTranslation } from 'translations/hooks';
import { insertOrUpdateLocalStorageValue } from 'technical/local-storage';
import {
  SCAN_PATTERN_KEY,
  SEGMENT_TYPES_KEY,
} from 'technical/local-storage/constants';
import {
  type ScanPatternSchema,
  type SegmentTypesSchema,
} from 'technical/local-storage/types';
import { useState } from 'react';
import { useGetScanConfigData } from './data/use-get-scan-config';
import { useGetSegmentDrawConfigData } from './data/use-get-segment-draw-config';

export const useSyncSettings = () => {
  const { t } = useTranslation();
  const [isSyncing, setIsSyncing] = useState(false);
  const { message } = App.useApp();

  const { refetch: fetchScanConfig } = useGetScanConfigData({ enabled: false });
  const { refetch: fetchSegmentDrawConfig } = useGetSegmentDrawConfigData({
    enabled: false,
  });

  const synchronize = async () => {
    setIsSyncing(true);
    try {
      const [scanConfigResponse, segmentDrawConfigResponse] = await Promise.all(
        [fetchScanConfig(), fetchSegmentDrawConfig()],
      );

      const newScanConfig = scanConfigResponse.data;
      const newSegmentDrawConfig = segmentDrawConfigResponse.data;

      if (newScanConfig) {
        insertOrUpdateLocalStorageValue<ScanPatternSchema>({
          key: SCAN_PATTERN_KEY,
          value: {
            startIndex: newScanConfig.startIndex,
            extractLength: newScanConfig.extractLength,
          },
        });
      }

      if (newSegmentDrawConfig) {
        const types =
          newSegmentDrawConfig?.drawConfigs?.map((config) => config.label) ||
          [];
        insertOrUpdateLocalStorageValue<SegmentTypesSchema>({
          key: SEGMENT_TYPES_KEY,
          value: [...new Set(types)],
        });
      }

      return {
        success: true,
        data: {
          scanPattern: newScanConfig,
          segmentTypes: newSegmentDrawConfig,
        },
      };
    } catch (error) {
      message.error(t('common.technicalError'));
      return { success: false, error };
    } finally {
      setIsSyncing(false);
    }
  };

  return { synchronize, isSyncing };
};
